/* ============================================================
   TEAK TALE THEME by Impulsebyte (impulsebyte.com) — theme.js  v1.0
   Vanilla JS — no dependencies
   ============================================================ */
(function () {
'use strict';

/* ─── Utilities ─── */
const $ = (sel, ctx = document) => ctx.querySelector(sel);
const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];
const on = (el, ev, fn, opts) => el && el.addEventListener(ev, fn, opts);
const off = (el, ev, fn) => el && el.removeEventListener(ev, fn);
const emit = (el, ev, detail) => el.dispatchEvent(new CustomEvent(ev, { detail, bubbles: true }));
const debounce = (fn, ms) => { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); }; };
const formatMoney = (cents) => window.ttMoney(cents);
const esc = (v) => String(v ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const PS_FIELDS = 'resources[options][fields]=title,product_type,variants.title,vendor,tag&resources[options][unavailable_products]=last';

/* Accessibility: closed drawers/modals are `inert` (not focusable, hidden from screen readers);
   open ones are not. Driven by the shared `is-open` class, so every component stays in sync. */
(() => {
  const ids = ['SearchDrawer', 'NavDrawer', 'QuickViewModal', 'CartDrawer', 'FilterDrawer', 'Lightbox'];
  const sync = el => { const open = el.classList.contains('is-open'); el.inert = !open; el.setAttribute('aria-hidden', open ? 'false' : 'true'); };
  const mo = new MutationObserver(list => list.forEach(m => sync(m.target)));
  const watch = () => ids.forEach(id => { const el = document.getElementById(id); if (el && !el._ttInert) { el._ttInert = true; sync(el); mo.observe(el, { attributes: true, attributeFilter: ['class'] }); } });
  watch();
  document.addEventListener('DOMContentLoaded', watch);
  document.addEventListener('shopify:section:load', watch);
})();
// Shopify's predictive search endpoint (/search/suggest.json) returns price_min/price_max
// as already-formatted decimal strings (e.g. "99.00"), NOT cents like /cart.js or product.js do.
const formatMoneyFromDecimal = (amount) => window.ttMoney(Math.round(parseFloat(amount || 0) * 100));
const trapFocus = (el) => {
  const focusable = $$('a,button,input,textarea,select,[tabindex]:not([tabindex="-1"])', el).filter(e => !e.disabled);
  const first = focusable[0], last = focusable[focusable.length - 1];
  const handler = (e) => {
    if (e.key !== 'Tab') return;
    if (e.shiftKey ? document.activeElement === first : document.activeElement === last) { e.preventDefault(); (e.shiftKey ? last : first)?.focus(); }
  };
  on(el, 'keydown', handler);
  return () => off(el, 'keydown', handler);
};

/* ─── Backdrop ─── */
class Backdrop {
  constructor() {
    this.el = $('#Backdrop');
    this.stack = 0;
    // Clicking the backdrop always dismisses everything — a fail-safe so the
    // overlay can never strand the shopper even if a counter drifts.
    on(this.el, 'click', () => { emit(document, 'tt:backdrop:click'); this.hideAll(); });
    on(document, 'keydown', e => { if (e.key === 'Escape') this.hideAll(); });
  }
  show() { this.stack++; this.el?.classList.add('is-open'); document.body.style.overflow = 'hidden'; }
  hide() { this.stack = Math.max(0, this.stack - 1); if (!this.stack) this.hideAll(); }
  hideAll() {
    this.stack = 0;
    this.el?.classList.remove('is-open');
    document.body.style.overflow = '';
  }
}
const backdrop = new Backdrop();
window.backdrop = backdrop;

/* ─── Toast ─── */
class Toast {
  constructor() { this.el = $('#Toast'); this._t = null; }
  show(msg, type = '', dur = 3000) {
    if (!this.el) return;
    clearTimeout(this._t);
    this.el.textContent = msg;
    this.el.className = 'toast is-visible' + (type ? ' toast--' + type : '');
    this._t = setTimeout(() => this.el.classList.remove('is-visible'), dur);
  }
}
const toast = new Toast();
window.ttToast = toast;
window.toast = toast;

/* ─── Search Drawer ─── */
class SearchDrawer {
  constructor() {
    this.drawer = $('#SearchDrawer');
    this.input = $('#SearchInput');
    this.results = $('#SearchResults');
    this.openBtns = $$('[data-open-search]');
    this.closeBtns = $$('[data-close-search]');
    this._untrap = null;
    this._search = debounce(this._doSearch.bind(this), 280);
    this._init();
  }
  _init() {
    this.openBtns.forEach(b => on(b, 'click', () => this.open()));
    this.closeBtns.forEach(b => on(b, 'click', () => this.close()));
    on(this.input, 'input', () => this._search());
    on(this.input, 'keydown', e => { if (e.key === 'Escape') this.close(); });
    on(document, 'tt:backdrop:click', () => this.close());
    on(document, 'keydown', e => { if (e.key === 'Escape') this.close(); });
  }
  open() {
    if (!this.drawer || this.drawer.classList.contains('is-open')) return;
    this.drawer.classList.add('is-open');
    this.drawer.setAttribute('aria-hidden', 'false');
    this.drawer.inert = false;
    backdrop.show();
    requestAnimationFrame(() => setTimeout(() => this.input?.focus(), 100));
    this._untrap = trapFocus(this.drawer);
  }
  close() {
    if (!this.drawer?.classList.contains('is-open')) return;
    this.drawer.classList.remove('is-open');
    backdrop.hide();
    this._untrap?.();
  }
  async _doSearch() {
    const q = this.input?.value.trim();
    if (!q || q.length < 2) { this.results?.classList.remove('is-visible'); this.drawer?.classList.remove('has-query'); return; }
    this.drawer?.classList.add('has-query');
    try {
      const url = `${window.TeakTale.routes.predictive_search}?q=${encodeURIComponent(q)}&resources[type]=product,collection,query&resources[limit]=6&${PS_FIELDS}`;
      const res = await fetch(url);
      const data = await res.json();
      if (q !== this.input?.value.trim()) return; // a newer search is running
      this._renderResults(data.resources?.results || {}, q);
    } catch(e) { /* silent */ }
  }
  _renderResults(r, q) {
    if (!this.results) return;
    const products = r.products || [], collections = r.collections || [], queries = r.queries || [];
    const all = `<a href="${window.TeakTale.routes.search_url}?q=${encodeURIComponent(q)}&type=product" class="search-results__all">View all results for “${esc(q)}”</a>`;
    if (!products.length && !collections.length && !queries.length) {
      this.results.innerHTML = `<p class="search-results__empty">No results for “${esc(q)}”. Try another word, like magnet or bookmark.</p>`;
      this.results.classList.add('is-visible');
      return;
    }
    let html = '';
    if (queries.length) html += `<div class="search-results__group"><span class="search-results__label">Suggestions</span><div class="search-results__terms">${queries.slice(0, 4).map(t => `<a href="${t.url}" class="search-pill">${esc(t.text)}</a>`).join('')}</div></div>`;
    if (collections.length) html += `<div class="search-results__group"><span class="search-results__label">Collections</span><div class="search-results__terms">${collections.slice(0, 4).map(c => `<a href="${c.url}" class="search-pill">${esc(c.title)}</a>`).join('')}</div></div>`;
    if (products.length) html += `<div class="search-results__group"><span class="search-results__label">Products</span>${products.map(p => `
      <a href="${p.url}" class="search-result-item">
        <img class="search-result-item__img" src="${p.featured_image?.url ? esc(p.featured_image.url.replace('?', '?width=96&')) : ''}" alt="" loading="lazy" width="48" height="48">
        <div>
          <div class="search-result-item__title">${esc(p.title)}</div>
          <div class="search-result-item__price">${formatMoneyFromDecimal(p.price_min ?? p.price)}</div>
        </div>
      </a>`).join('')}</div>`;
    this.results.innerHTML = html + all;
    this.results.classList.add('is-visible');
  }
}

/* ─── Nav Drawer ─── */
class NavDrawer {
  constructor() {
    this.drawer = $('#NavDrawer');
    this.openBtns = $$('[data-open-nav]');
    this.closeBtns = $$('[data-close-nav]');
    this._untrap = null;
    this._init();
  }
  _init() {
    this.openBtns.forEach(b => on(b, 'click', () => this.open()));
    this.closeBtns.forEach(b => on(b, 'click', () => this.close()));
    on(document, 'tt:backdrop:click', () => this.close());
    on(document, 'keydown', e => { if (e.key === 'Escape') this.close(); });
    // Sub-menu accordions — close siblings so only one group is open at a time
    $$('[data-nav-toggle]', this.drawer).forEach(btn => {
      on(btn, 'click', () => {
        const target = $('#' + btn.dataset.navToggle);
        if (!target) return;
        const willOpen = !target.classList.contains('is-open');
        $$('.nav-drawer__sub', this.drawer).forEach(s => {
          if (s !== target) {
            s.classList.remove('is-open');
            $(`[data-nav-toggle="${s.id}"]`, this.drawer)?.setAttribute('aria-expanded', 'false');
          }
        });
        target.classList.toggle('is-open', willOpen);
        btn.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
      });
    });
  }
  open() {
    if (!this.drawer || this.drawer.classList.contains('is-open')) return;
    this.drawer.classList.add('is-open');
    this.drawer.setAttribute('aria-hidden', 'false');
    this.drawer.inert = false;
    backdrop.show();
    this._untrap = trapFocus(this.drawer);
  }
  close() {
    if (!this.drawer?.classList.contains('is-open')) return;
    this.drawer.classList.remove('is-open');
    backdrop.hide();
    this._untrap?.();
  }
}

/* ─── Cart Drawer: see assets/cart-drawer.js (window.TTCartDrawer) ─── */

/* ─── Quick View ─── */
class QuickView {
  constructor() {
    this.modal = $('#QuickViewModal');
    this.img = $('#QvImg');
    this.name = $('#QvName');
    this.price = $('#QvPrice');
    this.origPrice = $('#QvOrig');
    this.discount = $('#QvDisc');
    this.link = $('#QvLink');
    this._untrap = null;
    this.product = null;
    this.variant = null;
    on(document, 'click', e => {
      const trigger = e.target.closest('[data-quick-view]');
      if (trigger) { e.preventDefault(); this.open(trigger.dataset.quickView); }
      if (e.target.closest('[data-close-qv]')) this.close();
      if (e.target.closest('#QvAtc')) { e.preventDefault(); this._add(); }
    });
    on(document, 'tt:backdrop:click', () => this.close());
    on(document, 'keydown', e => { if (e.key === 'Escape') this.close(); });
  }
  open(productUrl) {
    if (!this.modal) return;
    this._loadProduct(productUrl);
    if (this.modal.classList.contains('is-open')) return;
    this.modal.classList.add('is-open');
    this.modal.inert = false;
    this.modal.setAttribute('aria-hidden', 'false');
    backdrop.show();
    this._untrap = trapFocus(this.modal);
  }
  close() {
    if (!this.modal?.classList.contains('is-open')) return;
    this.modal.classList.remove('is-open');
    backdrop.hide();
    this._untrap?.();
  }
  async _loadProduct(url) {
    try {
      const res = await fetch(`${url}.js`);
      const p = await res.json();
      this.product = p;
      this.variant = p.variants.find(v => v.available) || p.variants[0];
      if (this.name) this.name.textContent = p.title;
      if (this.link) this.link.href = p.url;
      this._buildOptions(p);
      this._syncVariant();
    } catch(e) {}
  }
  /* Build option buttons for real (non-default) options */
  _buildOptions(p) {
    const wrap = $('#QvOptions');
    if (!wrap) return;
    const isDefaultOnly = p.variants.length === 1 && p.variants[0].title === 'Default Title';
    if (isDefaultOnly) { wrap.innerHTML = ''; return; }
    wrap.innerHTML = p.options.map((opt, i) => {
      const name = typeof opt === 'string' ? opt : opt.name;
      const values = [...new Set(p.variants.map(v => v.options[i]))];
      return `
        <div class="variant-group">
          <div class="variant-group__header">
            <span class="variant-group__label">${name}</span>
            <span class="variant-group__selected" data-qv-selected="${i}"></span>
          </div>
          <div class="variant-btns" role="radiogroup" aria-label="${name}">
            ${values.map(v => `<button type="button" class="variant-btn" data-qv-opt="${i}" data-qv-val="${String(v).replace(/"/g,'&quot;')}">${v}</button>`).join('')}
          </div>
        </div>`;
    }).join('');
    // Preselect the first available variant's options
    (this.variant?.options || []).forEach((val, i) => this._selectOpt(i, val, false));
    on(wrap, 'click', e => {
      const b = e.target.closest('[data-qv-opt]');
      if (b) this._selectOpt(parseInt(b.dataset.qvOpt), b.dataset.qvVal, true);
    });
  }
  _selectOpt(index, value, resolve) {
    $$(`[data-qv-opt="${index}"]`).forEach(b => b.classList.toggle('is-selected', b.dataset.qvVal === value));
    const label = $(`[data-qv-selected="${index}"]`);
    if (label) label.textContent = value;
    if (!resolve) return;
    const chosen = this.product.options.map((_, i) => $(`[data-qv-opt="${i}"].is-selected`)?.dataset.qvVal);
    const match = this.product.variants.find(v => v.options.every((o, i) => o === chosen[i]));
    if (match) { this.variant = match; this._syncVariant(); }
  }
  _syncVariant() {
    const p = this.product, v = this.variant;
    if (!p || !v) return;
    const img = v.featured_image?.src || p.featured_image || p.images?.[0] || '';
    if (this.img) { this.img.src = img ? img.replace('?', '?width=700&') : ''; this.img.alt = p.title; }
    if (this.price) this.price.textContent = formatMoney(v.price);
    if (this.origPrice) {
      const on = v.compare_at_price > v.price;
      this.origPrice.textContent = on ? formatMoney(v.compare_at_price) : '';
      this.origPrice.style.display = on ? '' : 'none';
    }
    if (this.discount) {
      const on = v.compare_at_price > v.price;
      this.discount.textContent = on ? Math.round((1 - v.price / v.compare_at_price) * 100) + '% off' : '';
      this.discount.style.display = on ? '' : 'none';
    }
    const stock = $('#QvStock');
    if (stock) stock.textContent = v.available ? '' : 'This option is sold out';
    const atc = $('#QvAtc');
    if (atc) {
      atc.disabled = !v.available;
      atc.textContent = v.available ? 'Add to Cart' : 'Sold Out';
      atc.dataset.variantId = v.id;
    }
  }
  async _add() {
    const atc = $('#QvAtc');
    const id = parseInt(atc?.dataset.variantId);
    if (!id || atc.disabled) return;
    atc.disabled = true; atc.textContent = 'Adding…';
    try {
      await window.cartDrawer?.addItem(id, 1);
      atc.textContent = 'Added';
      if (!window.cartDrawer) toast.show('Added to cart', 'success');
      setTimeout(() => { this.close(); window.cartDrawer?.afterAdd(); }, 400);
    } catch (err) {
      toast.show(err.message || 'Could not add to cart', 'error');
      atc.textContent = 'Add to Cart';
    }
    atc.disabled = false;
  }
}

/* ─── Header ─── */
class Header {
  constructor() {
    this.el = $('#SiteHeader');
    this._init();
  }
  _init() {
    // Sticky lives on the section wrapper (see base.css). Mirror the class for
    // browsers without :has() support.
    if (this.el?.classList.contains('site-header--sticky')) {
      this.el.closest('.shopify-section')?.classList.add('is-sticky');
    }
    // Hysteresis: condense past 60px, only expand again below 20px. Without the
    // gap the header would flicker between states when the collapsing row
    // changes the page height right at the threshold.
    let condensed = false;
    const onScroll = () => {
      const y = window.scrollY;
      if (!condensed && y > 60) { condensed = true; this.el?.classList.add('is-scrolled'); }
      else if (condensed && y < 20) { condensed = false; this.el?.classList.remove('is-scrolled'); }
    };
    on(window, 'scroll', onScroll, { passive: true });
    onScroll();
    // The header is multi-row and its height varies by breakpoint, so publish the
    // real measured height to --header-h. Sticky offsets (category strip, collection
    // sidebar, PDP) read this var instead of a hardcoded guess.
    // Measured once per breakpoint, NOT on every frame. Observing the header
    // with ResizeObserver rewrote --header-h during the condense transition,
    // which shifted sticky offsets repeatedly and made the browser disable
    // scroll anchoring ("too many consecutive adjustments" console warning).
    let lastH = 0;
    const setH = () => {
      if (!this.el) return;
      const h = Math.round(this.el.getBoundingClientRect().height);
      if (h === lastH) return;
      lastH = h;
      document.documentElement.style.setProperty('--header-h', h + 'px');
    };
    setH();
    on(window, 'resize', debounce(setH, 200), { passive: true });
    // Re-measure only after the condense transition has finished
    on(this.el, 'transitionend', e => { if (e.propertyName === 'min-height') setH(); });
  }
}

/* ─── Hero Slider ─── */
class HeroSlider {
  constructor(el) {
    this.el = el;
    if (!this.el) return;
    this.slides = $$('.hero__slide', el);
    this.dots = $$('.hero__dot', el);
    this.current = 0;
    this.total = this.slides.length;
    this.interval = null;
    this.autoplayOn = el.dataset.autoplay !== 'false';
    this.speed = (parseInt(el.dataset.autoplaySpeed) || 5) * 1000;
    if (this.total < 2) return;
    this._init();
  }
  _init() {
    const prev = $('.hero__prev', this.el), next = $('.hero__next', this.el);
    on(prev, 'click', () => this._go(this.current - 1));
    on(next, 'click', () => this._go(this.current + 1));
    this.dots.forEach((d, i) => on(d, 'click', () => this._go(i)));
    on(this.el, 'mouseenter', () => clearInterval(this.interval));
    on(this.el, 'mouseleave', () => this._autoplay());
    this._autoplay();
    // Touch swipe
    let startX = 0;
    on(this.el, 'touchstart', e => { startX = e.touches[0].clientX; }, { passive: true });
    on(this.el, 'touchend', e => { const diff = startX - e.changedTouches[0].clientX; if (Math.abs(diff) > 50) this._go(this.current + (diff > 0 ? 1 : -1)); });
  }
  _go(idx) {
    const n = (idx + this.total) % this.total;
    this.slides[this.current]?.classList.remove('is-active');
    this.dots[this.current]?.classList.remove('is-active');
    this.current = n;
    this.slides[n]?.classList.add('is-active');
    this.dots[n]?.classList.add('is-active');
    this.dots.forEach((d, i) => d.toggleAttribute('aria-current', i === n));
  }
  _autoplay() {
    clearInterval(this.interval);
    if (!this.autoplayOn) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.interval = setInterval(() => this._go(this.current + 1), this.speed);
  }
}

/* ─── Generic horizontal slider (collection scroller, product slider) ───
   Native scroll-snap does the work; arrows page by ~one viewport and disable
   themselves at each end. */
class Slider {
  constructor(el) {
    this.el = el;
    this.track = $('[data-slider-track]', el);
    this.prev = $('[data-slider-prev]', el);
    this.next = $('[data-slider-next]', el);
    if (!this.track) return;
    on(this.prev, 'click', () => this._page(-1));
    on(this.next, 'click', () => this._page(1));
    on(this.track, 'scroll', debounce(() => this._sync(), 80), { passive: true });
    on(window, 'resize', debounce(() => this._sync(), 150), { passive: true });
    this._initDrag();
    this._sync();
  }
  /* Click-and-drag to scroll (desktop). Touch already scrolls natively.
     Snapping is disabled mid-drag so the track follows the cursor 1:1. */
  _initDrag() {
    // Mouse only. Touch devices already scroll natively, and hooking pointer
    // events there swallowed taps on links/buttons.
    let down = false, moved = false, startX = 0, startScroll = 0;
    const THRESHOLD = 5;
    const start = e => {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      if (e.target.closest('button')) return; // let buttons receive their click
      down = true; moved = false;
      startX = e.clientX;
      startScroll = this.track.scrollLeft;
    };
    const move = e => {
      if (!down) return;
      const dx = e.clientX - startX;
      if (!moved && Math.abs(dx) > THRESHOLD) {
        // Only now do we treat it as a drag — before this, it's still a click.
        moved = true;
        this.track.classList.add('is-grabbing');
        this.track.style.scrollSnapType = 'none';
        this.track.style.scrollBehavior = 'auto';
      }
      if (moved) {
        e.preventDefault();
        this.track.scrollLeft = startScroll - dx;
      }
    };
    const end = () => {
      if (!down) return;
      const wasDrag = moved;
      down = false; moved = false;
      this.track.classList.remove('is-grabbing');
      this.track.style.scrollSnapType = '';
      this.track.style.scrollBehavior = '';
      // Swallow only the click that immediately follows a real drag
      if (wasDrag) {
        const swallow = ev => { ev.preventDefault(); ev.stopPropagation(); };
        this.track.addEventListener('click', swallow, { capture: true, once: true });
        setTimeout(() => this.track.removeEventListener('click', swallow, true), 50);
      }
      this._sync();
    };
    on(this.track, 'pointerdown', start);
    on(this.track, 'pointermove', move);
    on(window, 'pointerup', end);
    on(window, 'pointercancel', end);
    $$('img', this.track).forEach(n => on(n, 'dragstart', e => e.preventDefault()));
  }
  _page(dir) {
    const amount = this.track.clientWidth * 0.85;
    this.track.scrollBy({ left: dir * amount, behavior: 'smooth' });
  }
  _sync() {
    const max = this.track.scrollWidth - this.track.clientWidth - 2;
    if (this.prev) this.prev.disabled = this.track.scrollLeft <= 2;
    if (this.next) this.next.disabled = this.track.scrollLeft >= max;
  }
}

/* ─── Countdown Timer ─── */
class Countdown {
  constructor(el) {
    this.el = el;
    if (!this.el) return;
    const endStr = el.dataset.end;
    this.end = endStr ? new Date(endStr) : null;
    if (!this.end) return;
    this.h = $('#CdH', el); this.m = $('#CdM', el); this.s = $('#CdS', el);
    this.interval = null;
    this._tick();
    // Only run the 1s interval while the countdown is actually on screen; this is
    // wired to InViewMotion's IntersectionObserver via the tt:inview:change event.
    if (el.hasAttribute('data-inview-toggle')) {
      on(el, 'tt:inview:change', (e) => e.detail.inView ? this._start() : this._stop());
    } else {
      this._start();
    }
  }
  _start() { if (this.interval) return; this._tick(); this.interval = setInterval(() => this._tick(), 1000); }
  _stop() { clearInterval(this.interval); this.interval = null; }
  _tick() {
    const diff = Math.max(0, this.end - Date.now());
    const h = Math.floor(diff / 36e5), m = Math.floor((diff % 36e5) / 6e4), s = Math.floor((diff % 6e4) / 1e3);
    if (this.h) this.h.textContent = String(h).padStart(2,'0');
    if (this.m) this.m.textContent = String(m).padStart(2,'0');
    if (this.s) this.s.textContent = String(s).padStart(2,'0');
  }
}

/* ─── Scroll Animations ─── */
class ScrollAnimations {
  constructor() {
    const els = $$('.reveal');
    if (!('IntersectionObserver' in window)) {
      // No IO support: reveal immediately rather than leaving content stuck at opacity:0.
      els.forEach(el => el.classList.add('is-revealed'));
      return;
    }
    this.io = new IntersectionObserver((entries) => {
      entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('is-revealed'); this.io.unobserve(e.target); } });
    }, { threshold: 0.1, rootMargin: '0px 0px -40px 0px' });
    els.forEach(el => this.io.observe(el));
  }
}

/* ─── In-View Motion Controller (IntersectionObserver) ───
   Pauses CPU/GPU-hungry continuous animations (marquee scroll, countdown ticking,
   hero autoplay) while they're off-screen, and only starts them once visible.
   This cuts main-thread work on initial load and while scrolling past sections
   that aren't on screen yet, without affecting native image lazy-loading
   (which already uses loading="lazy" and doesn't need JS). */
class InViewMotion {
  constructor() {
    this.targets = $$('[data-inview-toggle]');
    if (!this.targets.length) return;
    if (!('IntersectionObserver' in window)) {
      this.targets.forEach(el => el.classList.add('is-inview'));
      return;
    }
    this.io = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        e.target.classList.toggle('is-inview', e.isIntersecting);
        emit(e.target, 'tt:inview:change', { inView: e.isIntersecting });
      });
    }, { threshold: 0, rootMargin: '80px 0px 80px 0px' });
    this.targets.forEach(el => this.io.observe(el));
  }
}

/* ─── Desktop inline header search with live suggestions ───
   Same predictive-search endpoint as the mobile drawer, rendered into a panel
   under the input. Supports arrow-key navigation and Enter to open. */
class HeaderSearch {
  constructor() {
    this.form = $('[data-header-search]');
    this.input = $('#HeaderSearchInput');
    this.panel = $('#HeaderSearchResults');
    this.clearBtn = $('[data-header-search-clear]');
    if (!this.form || !this.input || !this.panel) return;
    this.activeIdx = -1;
    this._search = debounce(this._doSearch.bind(this), 240);
    on(this.input, 'input', () => {
      this.clearBtn.hidden = !this.input.value;
      this._search();
    });
    on(this.input, 'focus', () => { if (this.input.value.trim().length >= 2) this._show(); });
    on(this.input, 'keydown', e => this._onKey(e));
    on(this.clearBtn, 'click', () => { this.input.value = ''; this.clearBtn.hidden = true; this._hide(); this.input.focus(); });
    on(document, 'click', e => { if (!this.form.contains(e.target)) this._hide(); });
  }
  async _doSearch() {
    const q = this.input.value.trim();
    if (q.length < 2) { this._hide(); return; }
    try {
      const url = `${window.TeakTale.routes.predictive_search}?q=${encodeURIComponent(q)}&resources[type]=product,collection,query&resources[limit]=6&${PS_FIELDS}`;
      const res = await fetch(url);
      const data = await res.json();
      if (q !== this.input.value.trim()) return;
      this._render(data.resources?.results || {}, q);
    } catch (e) { this._hide(); }
  }
  _render(r, q) {
    const products = r.products || [];
    const collections = r.collections || [];
    const queries = r.queries || [];
    if (!products.length && !collections.length && !queries.length) {
      this.panel.innerHTML = `<p class="header-search__empty">No results for “${esc(q)}”</p>`;
      this._show();
      return;
    }
    let html = '';
    if (queries.length) {
      html += `<div class="header-search__group"><span class="header-search__group-label">Suggestions</span>${
        queries.slice(0, 4).map(s => `<a href="${s.url}" class="header-search__row header-search__row--term" role="option">${esc(s.text)}</a>`).join('')
      }</div>`;
    }
    if (products.length) {
      html += `<div class="header-search__group"><span class="header-search__group-label">Products</span>${
        products.map(p => `
          <a href="${p.url}" class="header-search__row" role="option">
            <img class="header-search__thumb" src="${p.featured_image?.url ? p.featured_image.url.replace('?', '?width=96&') : ''}" alt="" width="44" height="44" loading="lazy">
            <span class="header-search__meta">
              <span class="header-search__name">${esc(p.title)}</span>
              <span class="header-search__price">${formatMoneyFromDecimal(p.price_min ?? p.price)}</span>
            </span>
          </a>`).join('')
      }</div>`;
    }
    if (collections.length) {
      html += `<div class="header-search__group"><span class="header-search__group-label">Collections</span>${
        collections.slice(0, 3).map(c => `<a href="${c.url}" class="header-search__row header-search__row--term" role="option">${esc(c.title)}</a>`).join('')
      }</div>`;
    }
    html += `<a href="${window.TeakTale.routes.search_url}?q=${encodeURIComponent(q)}&type=product" class="header-search__all">View all results for “${esc(q)}”</a>`;
    this.panel.innerHTML = html;
    this.activeIdx = -1;
    this._show();
  }
  _rows() { return $$('a', this.panel); }
  _onKey(e) {
    const rows = this._rows();
    if (e.key === 'Escape') { this._hide(); return; }
    if (!rows.length) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      this.activeIdx += e.key === 'ArrowDown' ? 1 : -1;
      if (this.activeIdx < 0) this.activeIdx = rows.length - 1;
      if (this.activeIdx >= rows.length) this.activeIdx = 0;
      rows.forEach((r, i) => r.classList.toggle('is-active', i === this.activeIdx));
      rows[this.activeIdx].scrollIntoView({ block: 'nearest' });
    } else if (e.key === 'Enter' && this.activeIdx > -1) {
      e.preventDefault();
      rows[this.activeIdx].click();
    }
  }
  _show() { this.panel.hidden = false; this.input.setAttribute('aria-expanded', 'true'); }
  _hide() { this.panel.hidden = true; this.input.setAttribute('aria-expanded', 'false'); this.activeIdx = -1; }
}

/* ─── Desktop mega menu: click + keyboard support (hover is CSS-only) ─── */
class MegaMenu {
  constructor() {
    this.items = $$('.site-nav__item');
    if (!this.items.length) return;
    this.items.forEach(item => {
      const btn = $('.site-nav__link', item);
      if (!btn || btn.tagName !== 'BUTTON') return;
      on(btn, 'click', e => {
        e.stopPropagation();
        const willOpen = !item.classList.contains('is-open');
        this._closeAll();
        item.classList.toggle('is-open', willOpen);
        btn.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
      });
      on(item, 'keydown', e => { if (e.key === 'Escape') { this._closeAll(); btn.focus(); } });
    });
    on(document, 'click', () => this._closeAll());
  }
  _closeAll() {
    this.items.forEach(i => {
      i.classList.remove('is-open');
      $('.site-nav__link', i)?.setAttribute('aria-expanded', 'false');
    });
  }
}

/* ─── Pincode / delivery-area selector ───
   Remembers the shopper's pincode in localStorage and reflects it in every
   instance of the pill (desktop row + mobile row stay in sync). */
class Pincode {
  constructor() {
    this.KEY = 'tt_pincode';
    this.roots = $$('[data-pincode-root]');
    if (!this.roots.length) return;
    this.saved = localStorage.getItem(this.KEY) || '';
    this._renderAll();
    this.roots.forEach(root => {
      const openBtn = $('[data-pincode-open]', root);
      const pop = $('.pincode__popover', root);
      const input = $('[data-pincode-input]', root);
      on(openBtn, 'click', e => {
        e.stopPropagation();
        const willOpen = pop.hidden;
        this._closeAll();
        pop.hidden = !willOpen;
        openBtn.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
        if (willOpen) { input.value = this.saved; input.focus(); }
      });
      on($('[data-pincode-save]', root), 'click', () => this._save(root));
      on(input, 'keydown', e => { if (e.key === 'Enter') { e.preventDefault(); this._save(root); } });
      on(input, 'input', () => { input.value = input.value.replace(/\D/g, '').slice(0, 6); });
      on(pop, 'click', e => e.stopPropagation());
    });
    on(document, 'click', () => this._closeAll());
    on(document, 'keydown', e => { if (e.key === 'Escape') this._closeAll(); });
  }
  _save(root) {
    const input = $('[data-pincode-input]', root);
    const msg = $('[data-pincode-msg]', root);
    const val = (input.value || '').trim();
    if (!/^\d{6}$/.test(val)) {
      msg.textContent = 'Please enter a valid 6-digit pincode.';
      msg.className = 'pincode__msg is-error';
      return;
    }
    this.saved = val;
    localStorage.setItem(this.KEY, val);
    msg.textContent = '✓ Delivering to ' + val;
    msg.className = 'pincode__msg is-ok';
    this._renderAll();
    emit(document, 'tt:pincode:updated', { pincode: val });
    setTimeout(() => this._closeAll(), 900);
  }
  _renderAll() {
    $$('[data-pincode-label]').forEach(el => {
      el.textContent = this.saved ? `Deliver to ${this.saved}` : 'Enter your Pincode';
    });
  }
  _closeAll() {
    this.roots.forEach(root => {
      const pop = $('.pincode__popover', root);
      if (pop) pop.hidden = true;
      $('[data-pincode-open]', root)?.setAttribute('aria-expanded', 'false');
    });
  }
}

/* ─── Hero Parallax (gated by the "Enable parallax effects" theme setting) ─── */
class Parallax {
  constructor() {
    if (document.body.dataset.parallax !== 'true') return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.els = $$('.hero__slide-bg');
    if (!this.els.length) return;
    let ticking = false;
    on(window, 'scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const y = window.scrollY;
        if (y < window.innerHeight) this.els.forEach(el => { el.style.transform = `translateY(${y * 0.18}px)`; });
        ticking = false;
      });
    }, { passive: true });
  }
}

/* ─── Wishlist (localStorage, stores product HANDLES so the wishlist
       page can fetch product data via /products/{handle}.js) ─── */
class Wishlist {
  constructor() {
    this.KEY = 'tt_wishlist';
    this.items = JSON.parse(localStorage.getItem(this.KEY) || '[]');
    this._updateAllBtns();
    on(document, 'click', e => {
      const btn = e.target.closest('[data-wishlist]');
      if (btn) { e.preventDefault(); this.toggle(btn.dataset.wishlist, btn); }
    });
  }
  toggle(handle, btn) {
    const idx = this.items.indexOf(handle);
    if (idx === -1) { this.items.push(handle); toast.show('Added to wishlist', 'success'); }
    else { this.items.splice(idx, 1); toast.show('Removed from wishlist'); }
    localStorage.setItem(this.KEY, JSON.stringify(this.items));
    this._updateAllBtns();
    emit(document, 'tt:wishlist:updated', { handle, added: idx === -1 });
  }
  remove(handle) {
    this.items = this.items.filter(h => h !== handle);
    localStorage.setItem(this.KEY, JSON.stringify(this.items));
    this._updateAllBtns();
    emit(document, 'tt:wishlist:updated', { handle, added: false });
  }
  has(handle) { return this.items.includes(handle); }
  get() { return [...this.items]; }
  _updateBtn(btn, active) {
    if (!btn) return;
    btn.classList.toggle('is-active', active);
    btn.setAttribute('aria-pressed', active ? 'true' : 'false');
  }
  _updateAllBtns() {
    $$('[data-wishlist]').forEach(btn => this._updateBtn(btn, this.has(btn.dataset.wishlist)));
  }
}

/* ─── Recently Viewed (localStorage, stores product HANDLES) ─── */
class RecentlyViewed {
  constructor() {
    this.KEY = 'tt_recently_viewed';
    this.MAX = 12;
    // #PdpPage carries data-product-handle on product pages
    const handle = $('#PdpPage')?.dataset.productHandle;
    if (handle) this.add(handle);
    const pid = $('#PdpPage')?.dataset.productId;
    if (pid) { try { const k = 'tt_recent_ids', l = JSON.parse(localStorage.getItem(k) || '[]').map(String).filter(x => x !== pid); localStorage.setItem(k, JSON.stringify([pid, ...l].slice(0, 16))); } catch (e) {} }
  }
  add(handle) {
    let items = this.get();
    items = [handle, ...items.filter(i => i !== handle)].slice(0, this.MAX);
    localStorage.setItem(this.KEY, JSON.stringify(items));
  }
  get() {
    // Filter out legacy numeric-id entries from the old storage format
    return (JSON.parse(localStorage.getItem(this.KEY) || '[]')).filter(i => typeof i === 'string' && !/^\d+$/.test(i));
  }
}

/* ═══════════ Client-rendered product grids ═══════════
   Shared renderer + IO-deferred data fetching for Recently Viewed,
   You May Also Like (tag-matched) and the Wishlist page. */

/* Run cb once, the first time el enters the viewport (IntersectionObserver). */
const onFirstInView = (el, cb, margin = '200px') => {
  if (!el) return;
  if (!('IntersectionObserver' in window)) { cb(); return; }
  const io = new IntersectionObserver((entries) => {
    if (entries.some(e => e.isIntersecting)) { io.disconnect(); cb(); }
  }, { rootMargin: `${margin} 0px ${margin} 0px` });
  io.observe(el);
};

/* Normalizers: /products/{handle}.js and /recommendations return prices in cents;
   predictive search (/search/suggest.json) returns decimal strings. */
const normFromProductJs = (p) => ({
  handle: p.handle, url: `/products/${p.handle}`, title: p.title,
  img: p.featured_image || p.images?.[0] || '',
  priceHtml: formatMoney(p.price_min ?? p.price),
  compareHtml: (p.compare_at_price_min ?? p.compare_at_price) > (p.price_min ?? p.price) ? formatMoney(p.compare_at_price_min ?? p.compare_at_price) : '',
  available: p.available !== false,
  variantId: (p.variants || []).find(v => v.available)?.id || p.variants?.[0]?.id || '',
  hasVariants: !!(p.variants && p.variants.length > 1)
});
const normFromSuggest = (p) => ({
  handle: p.handle, url: p.url, title: p.title,
  img: p.featured_image?.url || '',
  priceHtml: formatMoneyFromDecimal(p.price_min ?? p.price),
  compareHtml: parseFloat(p.compare_at_price_min || 0) > parseFloat(p.price_min || 0) ? formatMoneyFromDecimal(p.compare_at_price_min) : '',
  available: p.available !== false,
  // Predictive search gives one variant_id when there's a single variant
  variantId: p.variants?.[0]?.id || '',
  hasVariants: (p.variants?.length || 0) > 1
});

const ICON_CART_SVG = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><path d="M3 6h18"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>';
const ICON_HEART_SVG = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>';

const clientCardHTML = (p, opts = {}) => {
  const img = p.img ? p.img.replace(/(\.[a-z]{3,4})(\?|$)/i, '$1$2').replace('?', '?width=480&') : '';
  return `
  <article class="prod-card client-card" data-handle="${p.handle}">
    <div class="prod-card__media" data-ratio="square">
      <a href="${p.url}" aria-label="${p.title.replace(/"/g, '&quot;')}" tabindex="-1">
        ${img ? `<img class="prod-card__img" src="${img}" alt="${p.title.replace(/"/g, '&quot;')}" width="480" height="480" loading="lazy">` : `<div class="prod-card__img" style="background:var(--c-primary-50);aspect-ratio:1"></div>`}
      </a>
      ${opts.removable
        ? `<button type="button" class="client-card__remove" data-wishlist-remove="${p.handle}" aria-label="Remove from wishlist" title="Remove">&times;</button>`
        : `<button type="button" class="prod-card__wishlist" data-wishlist="${p.handle}" aria-label="Add to wishlist" aria-pressed="false">${ICON_HEART_SVG}</button>`}
      ${!p.available ? '<div class="prod-card__badges"><span class="badge badge--primary">Sold Out</span></div>' : ''}
    </div>
    <div class="prod-card__body">
      <h3 class="prod-card__title"><a href="${p.url}">${p.title}</a></h3>
      <div class="prod-card__footer">
        <div class="prod-card__price">
          <span class="prod-card__price-current">${p.priceHtml}</span>
          ${p.compareHtml ? `<span class="prod-card__price-compare">${p.compareHtml}</span>` : ''}
        </div>
        <button
          type="button"
          class="prod-card__atc"
          data-atc-card
          data-variant-id="${p.variantId || ''}"
          data-product-url="${p.url}"
          data-has-variants="${p.hasVariants ? 'true' : 'false'}"
          ${!p.available ? 'disabled' : ''}
          aria-label="${p.hasVariants ? 'Choose options' : 'Add to cart'}">${ICON_CART_SVG}</button>
      </div>
    </div>
  </article>`;
};

const fetchProductByHandle = async (handle) => {
  try {
    const res = await fetch(`/products/${handle}.js`);
    if (!res.ok) return null;
    return await res.json();
  } catch (e) { return null; }
};

/* ─── You May Also Like (tag-matched, recommendations fallback) ─── */
class YmalSection {
  constructor(root) {
    this.root = root;
    if (!this.root) return;
    this.grid = $('#YmalGrid', root);
    this.tags = (root.dataset.tags || '').split('|').map(t => t.trim()).filter(Boolean);
    this.exclude = root.dataset.exclude || '';
    this.productId = root.dataset.productId || '';
    this.limit = parseInt(root.dataset.limit) || 4;
    onFirstInView(root.parentElement || root, () => this._load());
  }
  async _load() {
    const found = new Map(); // handle → normalized product
    // 1. Tag matches via predictive search (fields=tag)
    for (const tag of this.tags.slice(0, 3)) {
      if (found.size >= this.limit) break;
      try {
        const url = `${window.TeakTale.routes.predictive_search}?q=${encodeURIComponent(tag)}&resources[type]=product&resources[limit]=8&resources[options][fields]=tag`;
        const res = await fetch(url);
        const data = await res.json();
        (data.resources?.results?.products || []).forEach(p => {
          if (p.handle !== this.exclude && !found.has(p.handle)) found.set(p.handle, normFromSuggest(p));
        });
      } catch (e) { /* ignore, fallback below */ }
    }
    // 2. Top up with Shopify's native recommendations
    if (found.size < this.limit && this.productId) {
      try {
        const res = await fetch(`/recommendations/products.json?product_id=${this.productId}&limit=${this.limit + 2}`);
        const data = await res.json();
        (data.products || []).forEach(p => {
          if (p.handle !== this.exclude && !found.has(p.handle)) found.set(p.handle, normFromProductJs(p));
        });
      } catch (e) { /* ignore */ }
    }
    const products = [...found.values()].slice(0, this.limit);
    if (!products.length) return; // stays hidden
    this.grid.innerHTML = products.map(p => clientCardHTML(p)).join('');
    this.root.hidden = false;
    this.root.classList.add('is-loaded');
    window._wishlist?._updateAllBtns();
  }
}

/* ─── Wishlist page ─── */
class WishlistPage {
  constructor(root) {
    this.root = root;
    if (!this.root) return;
    this.grid = $('#WishlistGrid', root);
    this.empty = $('#WishlistEmpty', root);
    this.count = $('#WishlistCount', root);
    this._render();
    on(document, 'tt:wishlist:updated', () => this._render());
    on(this.grid, 'click', e => {
      const btn = e.target.closest('[data-wishlist-remove]');
      if (btn) { e.preventDefault(); window._wishlist?.remove(btn.dataset.wishlistRemove); }
    });
  }
  async _render() {
    const handles = (window._wishlist?.get() || []).filter(h => typeof h === 'string' && !/^\d+$/.test(h));
    if (this.count) this.count.textContent = handles.length ? `(${handles.length})` : '';
    if (!handles.length) {
      this.grid.innerHTML = '';
      if (this.empty) this.empty.hidden = false;
      return;
    }
    if (this.empty) this.empty.hidden = true;
    const products = (await Promise.all(handles.map(fetchProductByHandle))).filter(Boolean);
    this.grid.innerHTML = products.map(p => clientCardHTML(normFromProductJs(p), { removable: true })).join('');
  }
}

/* ─── Marquee pause on hover (already CSS but ensure for old Safari) ───
   Defers to the is-inview class (set by InViewMotion) rather than forcing
   'running' on mouseleave, so an off-screen marquee stays paused. */
$$('.marquee-inner').forEach(el => {
  on(el, 'mouseenter', () => el.style.animationPlayState = 'paused');
  on(el, 'mouseleave', () => { el.style.animationPlayState = el.classList.contains('is-inview') ? 'running' : 'paused'; });
});

/* ─── Featured-products filter tabs ───
   Tabs existed in markup but had NO handler. Filters the sibling grid's
   cards by tag (cards carry data-tags, handleized). */
on(document, 'click', e => {
  const tab = e.target.closest('[data-filter-tab]');
  if (!tab) return;
  const wrap = tab.closest('.filter-tabs');
  const grid = wrap?.parentElement?.querySelector('.products-grid');
  if (!grid) return;
  $$('[data-filter-tab]', wrap).forEach(t => t.classList.toggle('is-active', t === tab));
  const val = tab.dataset.filterTab;
  $$('.prod-card', grid).forEach(card => {
    const tags = (card.dataset.tags || '').split('|');
    card.style.display = (val === 'all' || tags.includes(val)) ? '' : 'none';
  });
});

/* ─── Coupon copy-to-clipboard (PDP coupon blocks) ─── */
on(document, 'click', async e => {
  const btn = e.target.closest('[data-copy-coupon]');
  if (!btn) return;
  try {
    await navigator.clipboard.writeText(btn.dataset.copyCoupon);
    const orig = btn.textContent;
    btn.textContent = 'Copied ✓';
    btn.classList.add('is-copied');
    toast.show(`Coupon ${btn.dataset.copyCoupon} copied`, 'success');
    setTimeout(() => { btn.textContent = orig; btn.classList.remove('is-copied'); }, 2000);
  } catch (err) {
    toast.show('Could not copy — code: ' + btn.dataset.copyCoupon);
  }
});

/* ─── Card quick add-to-cart (product cards, upsell, anywhere) ───
   Delegated on document, so it also covers cards injected by Load More /
   infinite scroll. If the product has real variants we open the quick-view
   picker first instead of blindly adding the first variant. */
on(document, 'click', async e => {
  const btn = e.target.closest('[data-atc-card]');
  if (!btn || btn.disabled) return;
  e.preventDefault();
  e.stopPropagation();

  // Personalised product → needs text/photo, so open the product page
  if (btn.hasAttribute('data-personalise') && btn.dataset.productUrl) { window.location.href = btn.dataset.productUrl; return; }

  // Product has options → let the shopper choose before adding
  if (btn.dataset.hasVariants === 'true') {
    const url = btn.dataset.productUrl;
    if (url) { window._quickView?.open(url); return; }
  }

  const variantId = parseInt(btn.dataset.variantId);
  if (!variantId) {
    // No usable variant id — let the shopper pick in quick view instead
    if (btn.dataset.productUrl) window._quickView?.open(btn.dataset.productUrl);
    return;
  }
  const orig = btn.innerHTML;
  btn.disabled = true;
  btn.classList.add('is-loading');
  try {
    await window.cartDrawer?.addItem(variantId, 1);
    btn.classList.add('is-added');
    if (!window.cartDrawer) toast.show('Added to cart', 'success');
    window.cartDrawer?.afterAdd();
    setTimeout(() => btn.classList.remove('is-added'), 1600);
  } catch (err) {
    toast.show(err.message || 'Could not add to cart', 'error');
  }
  btn.innerHTML = orig;
  btn.classList.remove('is-loading');
  btn.disabled = false;
});

/* ─── Product card hover image: fetched on the first mouse hover only ─── */
if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
  on(document, 'pointerover', e => {
    const card = e.target.closest?.('.prod-card');
    if (!card || card.dataset.hoverReady) return;
    card.dataset.hoverReady = '1';
    const alt = card.querySelector('[data-hover-src]');
    if (!alt) return;
    alt.srcset = alt.dataset.hoverSrcset || '';
    alt.src = alt.dataset.hoverSrc;
    const done = () => card.classList.add('has-hover-img');
    if (alt.complete) done(); else alt.addEventListener('load', done, { once: true });
  }, { passive: true });
}

/* ─── FAQ Accordion ─── */
$$('.faq-item__btn').forEach(btn => {
  on(btn, 'click', () => {
    const item = btn.closest('.faq-item');
    const body = item.querySelector('.faq-item__body');
    const isOpen = item.classList.contains('is-open');
    item.classList.toggle('is-open', !isOpen);
    btn.setAttribute('aria-expanded', !isOpen ? 'true' : 'false');
    if (body) body.style.maxHeight = !isOpen ? body.scrollHeight + 'px' : '0';
  });
});

/* ─── Init ─── */
document.addEventListener('DOMContentLoaded', () => {
  // Cart first: revenue-critical, so an error elsewhere can never block it
  window.cartDrawer = window.TTCartDrawer ? new window.TTCartDrawer() : null;
  window._search = new SearchDrawer();
  window._nav = new NavDrawer();
  window._quickView = new QuickView();
  window._header = new Header();
  window._wishlist = new Wishlist();
  window._recentlyViewed = new RecentlyViewed();
  window._scroll = new ScrollAnimations();
  window._megaMenu = new MegaMenu();
  window._headerSearch = new HeaderSearch();
  $$('.hero').forEach(el => new HeroSlider(el));
  // Non-critical work waits for an idle moment so the first tap is never blocked (lower TBT / INP)
  const idle = cb => ('requestIdleCallback' in window) ? requestIdleCallback(cb, { timeout: 1500 }) : setTimeout(cb, 200);
  idle(() => {
    window._inviewMotion = new InViewMotion();
    window._parallax = new Parallax();
    new YmalSection($('#Ymal'));
    new WishlistPage($('#WishlistPage'));
    $$('[data-slider]').forEach(el => new Slider(el));
    $$('[data-countdown]').forEach(el => new Countdown(el));
  });
});

})(); // end IIFE
